import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { useNavigate } from 'react-router-dom';
import './proxy-block.css';

/**
 * Универсальный блок статистики по макету Figma 26-04 wa Proxy Manager.
 * Два режима кнопки:
 *  - onRecheck → зелёная кнопка «Перепроверить»
 *  - linkTo    → синяя ссылка «В раздел →»
 *  - linkAriaLabel → подпись для aria-label ссылки (если не задана — по title)
 */
const ProxyBlock = ({
  title = 'прокси',
  total = 100,
  available = 75,
  unavailable = 25,
  labelAvailable = 'Доступно',
  labelUnavailable = 'Недоступно',
  linkTo,
  linkAriaLabel,
  onRecheck,
  recheckLoading = false,
}) => {
  const navigate = useNavigate();
  const isModifiedClick = (e) => e.metaKey || e.altKey || e.ctrlKey || e.shiftKey;
  const knownSum = (available || 0) + (unavailable || 0);
  const percentAvailable = knownSum > 0 ? Math.round((available / knownSum) * 100) : 0;
  const percentUnavailable = knownSum > 0 ? 100 - percentAvailable : 0;
  const diagramCanvasRef = useRef(null);
  const diagramChartRef = useRef(null);

  const handleLinkClick = (e) => {
    if (e.button !== 0 || isModifiedClick(e)) {
      return;
    }
    e.preventDefault();
    if (linkTo) {
      navigate(linkTo);
    }
  };

  const handleLinkKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && linkTo) {
      e.preventDefault();
      navigate(linkTo);
    }
  };

  const handleRecheck = () => {
    if (onRecheck) {
      onRecheck();
    }
  };

  useEffect(() => {
    if (!diagramCanvasRef.current) {
      return undefined;
    }

    const chartOptions = {
      type: 'doughnut',
      data: {
        labels: [labelAvailable, labelUnavailable],
        datasets: [
          {
            data: [percentAvailable, percentUnavailable],
            backgroundColor: ['rgba(12, 190, 144, 0.35)', 'rgba(243, 63, 78, 0.35)'],
            hoverBackgroundColor: ['rgba(12, 190, 144, 0.5)', 'rgba(243, 63, 78, 0.5)'],
            borderColor: ['#0CBE90', '#F33F4E'],
            borderWidth: 1,
            borderRadius: 2,
            spacing: 4,
          },
        ],
      },
      options: {
        cutout: '60%',
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        animation: {
          animateScale: true,
          animateRotate: true,
        },
      },
    };

    if (diagramChartRef.current) {
      diagramChartRef.current.data.datasets[0].data = [percentAvailable, percentUnavailable];
      diagramChartRef.current.update();
      return undefined;
    }

    const chart = new Chart(diagramCanvasRef.current, chartOptions);
    diagramChartRef.current = chart;

    return () => {
      if (diagramChartRef.current) {
        diagramChartRef.current.destroy();
        diagramChartRef.current = null;
      }
    };
  }, [percentAvailable, percentUnavailable, labelAvailable, labelUnavailable]);

  return (
    <section className="proxy-block" aria-label={`Блок статистики: ${title}`}>
      <div className="proxy-block__title">
        <h2 className="proxy-block__title-text">{title}</h2>

        {onRecheck && (
          <button
            type="button"
            className="proxy-block__btn proxy-block__btn--recheck"
            onClick={handleRecheck}
            disabled={recheckLoading}
            aria-label={`Перепроверить ${title}`}
          >
            {recheckLoading ? 'Проверка...' : 'Перепроверить'}
          </button>
        )}

        {!onRecheck && linkTo && (
          <a
            href={linkTo}
            className="proxy-block__btn proxy-block__btn--link"
            onClick={handleLinkClick}
            onKeyDown={handleLinkKeyDown}
            tabIndex={0}
            aria-label={linkAriaLabel || `Перейти в раздел ${title}`}
          >
            В раздел
            <svg
              className="proxy-block__btn-icon"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M4.5 3L7.5 6L4.5 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        )}
      </div>

      <div className="proxy-block__info">
        <div className="proxy-block__table">
          <div className="proxy-block__table-col">
            <div className="proxy-block__table-cell proxy-block__table-cell--header">
              Всего
            </div>
            <div className="proxy-block__table-cell proxy-block__table-cell--good">
              {labelAvailable}
            </div>
            <div className="proxy-block__table-cell proxy-block__table-cell--bad">
              {labelUnavailable}
            </div>
          </div>
          <div className="proxy-block__table-col proxy-block__table-col--fixed">
            <div className="proxy-block__table-cell proxy-block__table-cell--header">
              {total}
            </div>
            <div className="proxy-block__table-cell proxy-block__table-cell--good">
              {available}
            </div>
            <div className="proxy-block__table-cell proxy-block__table-cell--bad">
              {unavailable}
            </div>
          </div>
          <div className="proxy-block__table-col proxy-block__table-col--fixed">
            <div className="proxy-block__table-cell proxy-block__table-cell--header">
              %
            </div>
            <div className="proxy-block__table-cell proxy-block__table-cell--good">
              {percentAvailable}
            </div>
            <div className="proxy-block__table-cell proxy-block__table-cell--bad">
              {percentUnavailable}
            </div>
          </div>
        </div>

        <div className="proxy-block__diagram" role="img" aria-label={`${labelAvailable} ${percentAvailable}%, ${labelUnavailable} ${percentUnavailable}%`}>
          <canvas className="proxy-block__diagram-canvas" ref={diagramCanvasRef} width="114.3" height="114.3" aria-hidden="true" />
          <span className="proxy-block__diagram-value">{percentAvailable}%</span>
        </div>
      </div>
    </section>
  );
};

export default ProxyBlock;
